import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../utils/api';
import { ArrowLeft } from 'lucide-react';

const AuthPage = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = isLogin
        ? await api.login({ email: formData.email, password: formData.password })
        : await api.signup(formData);

      localStorage.setItem('apexion_token', response.data.token);
      localStorage.setItem('apexion_user', JSON.stringify(response.data.user));
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA] flex items-center justify-center p-4">
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full max-w-md">
        <Button
          data-testid="auth-back-btn"
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-4 text-[#94A3B8] hover:text-[#F5F7FA]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
        </Button>

        <Card className="bg-[#15171C] border-[#1E293B]">
          <CardHeader>
            <CardTitle className="text-2xl font-['Outfit'] text-center">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </CardTitle>
            <CardDescription className="text-center text-[#94A3B8]">
              {isLogin ? 'Sign in to your Apexion account' : 'Start your 7-day paper trading trial'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="full_name" className="text-[#F5F7FA]">
                    Full Name
                  </Label>
                  <Input
                    data-testid="auth-fullname-input"
                    id="full_name"
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required={!isLogin}
                    className="bg-[#0B0D12] border-[#1E293B] text-[#F5F7FA] rounded-sm"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-[#F5F7FA]">
                  Email
                </Label>
                <Input
                  data-testid="auth-email-input"
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-[#0B0D12] border-[#1E293B] text-[#F5F7FA] rounded-sm"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-[#F5F7FA]">
                  Password
                </Label>
                <Input
                  data-testid="auth-password-input"
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="bg-[#0B0D12] border-[#1E293B] text-[#F5F7FA] rounded-sm"
                />
              </div>

              <Button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-[#2F6BFF] hover:bg-[#2F6BFF]/90 rounded-sm"
              >
                {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                data-testid="auth-toggle-btn"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-[#2F6BFF] hover:underline"
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            </div>

            <div className="mt-6 p-4 bg-[#0B0D12] border border-[#1E293B] rounded-sm">
              <p className="text-xs text-[#94A3B8] text-center">
                By continuing, you agree that trading involves risk. Apexion does not provide financial advice or
                custody funds.
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AuthPage;